import { DropEvent, DropzoneRef, useDropzone } from 'react-dropzone';
import { FaFile, IconType, Text } from '@bymason-private/legacy-pattern-components';
import { addAlpha } from '@bymason-private/controller-v1-theme-provider';
import { useState, useCallback, useEffect, useRef } from 'react';

type TimeoutID = ReturnType<typeof setTimeout>;

const SINGLE_FILE = 'Drag & drop a single file or click here';
const MULTI_FILE = 'Drag & drop files or click here';
const MULTI_FILE_MAX = 5;

export function DropzoneContainer({
  FileIcon = FaFile,
  accept,
  disabled,
  maxFiles = 1,
  multiple = false,
  onFileDrop,
  dropzoneRef,
  showDropzone,
  children,
  ...divProps
}: DropzoneContainerProps) {
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept,
    maxFiles,
    multiple,
    disabled,
    // Don't let click open file dialog if dropzone overlay not showing,
    // otherwise click on child elements opens the dialog
    noClick: !showDropzone,
    onDrop: (acceptedFiles, fileRejections, event) => {
      const tooManyFiles = acceptedFiles.length > maxFiles;
      const rejectionCount = fileRejections.length;
      const rejectionReason = tooManyFiles
        ? 'over-max-drop-count'
        : rejectionCount
        ? 'incompatible-files'
        : undefined;

      if (rejectionCount) {
        console.log('Some files were incompatible:', fileRejections);
      }

      onFileDrop &&
        onFileDrop({
          files: tooManyFiles ? ([] as typeof acceptedFiles) : acceptedFiles,
          rejectionCount,
          rejectionReason,
          event,
        });
    },
  });
  const debounceTimeout = useRef<TimeoutID | undefined>();
  const [showOverlayZone, setShowOverlayZone] = useState(showDropzone || false);
  const [dragCountOverMax, setDragCountOverMax] = useState(false);

  const handleDragEnter: React.DragEventHandler<HTMLSpanElement> = useCallback(
    (evt) => {
      const maxFileCount = multiple ? MULTI_FILE_MAX : 1;
      if (evt.dataTransfer.items.length > maxFileCount) {
        setDragCountOverMax(true);
      } else {
        setDragCountOverMax(false);
      }
    },
    [multiple]
  );

  const debounceShowDropzone = useCallback((value: boolean) => {
    clearTimeout(debounceTimeout.current as TimeoutID);
    if (value) {
      setShowOverlayZone(value);
    }
    debounceTimeout.current = setTimeout(() => {
      setShowOverlayZone(value);
    }, 1000);
  }, []);

  useEffect(() => {
    /**
     * Since we only want to show the dropzone while the drag is active,
     * showDropzone must be debounced as browsers repeatedly trigger isDragActive
     * while dragging, causing flicker of the dropzone component
     */
    debounceShowDropzone(showDropzone || isDragActive);
  }, [showDropzone, isDragActive, debounceShowDropzone]);

  useEffect(() => {
    if (dropzoneRef) {
      dropzoneRef.current = {
        open,
      };
    }
  }, [dropzoneRef, open]);

  return (
    <div {...getRootProps()} {...divProps} style={{ position: 'relative' }}>
      {children}
      <div>
        <input {...getInputProps()} />
        {showOverlayZone && (
          <Text
            onDragEnter={handleDragEnter}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              alignContent: 'center',
              width: '100%',
              height: '100%',
              minHeight: '100px',
              columnGap: 1,
              padding: 1,
              color: (theme) => (dragCountOverMax ? theme.palette.grey['300'] : undefined),
              border: (theme) =>
                `1px dashed ${dragCountOverMax ? theme.palette.error.main : theme.palette.grey['600']}`,
              backgroundColor: (theme) => addAlpha(theme.palette.background.default, 0.7),
            }}
          >
            {dragCountOverMax ? (
              <>
                <span>Too many files selected!</span>
                {multiple && <span> ({MULTI_FILE_MAX} max)</span>}
              </>
            ) : (
              <>
                <FileIcon />
                <span>{multiple ? MULTI_FILE : SINGLE_FILE}</span>
                {accept && <span>({Array.isArray(accept) ? accept.join(', ') : accept})</span>}
              </>
            )}
          </Text>
        )}
      </div>
    </div>
  );
}

export interface DropResult<T> {
  files: T[];
  rejectionCount: number;
  rejectionReason?: 'over-max-drop-count' | 'incompatible-files';
  event: DropEvent;
}

export type DropzoneDropEvent = <T extends File>(result: DropResult<T>) => void;

interface DropzoneContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  accept?: string | string[];
  disabled?: boolean;
  multiple?: boolean;
  maxFiles?: number;
  FileIcon?: IconType;
  onFileDrop?: DropzoneDropEvent;
  dropzoneRef?: React.MutableRefObject<DropzoneRef | undefined>;
  showDropzone?: boolean;
  children?: React.ReactNode;
}
